import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SourcesLink } from "../components/SourcesLink";
import { DataStamp } from "../components/DataLabel";
import { ArrowUpRight, Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { KplAlignment } from "../components/KplAlignment";
import { CountyAllocation } from "../components/CountyAllocation";
import { PriorityEighteen } from "../components/PriorityEighteen";
import { BrazilBenchmark } from "../components/BrazilBenchmark";
import { FutsalCourtFeature } from "../components/FutsalCourtFeature";
import { FacilityLibrary } from "../components/FacilityLibrary";

const usd = (n) =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : n >= 1e3 ? `$${Math.round(n).toLocaleString()}` : `$${Math.round(n || 0)}`;
const ksh = (n) =>
  n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : n >= 1e6 ? `KSh ${(n / 1e6).toFixed(1)}m` : `KSh ${Math.round(n || 0).toLocaleString()}`;
const int = (n) => Math.round(n || 0).toLocaleString();
const pct = (n) => `${Math.round((n || 0) * 100)}%`;

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-6" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
    {note ? <p className="mt-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

const STEP_FOR = {
  "Stackable 5-a-side and futsal courts": { label: "National game, step 3", to: "/plans?track=national" },
  "Community 11-a-side pitch upgrades": { label: "National game, step 3", to: "/plans?track=national" },
  "Indoor football arenas": { label: "National game, step 3", to: "/plans?track=national" },
  "County academies and coach education": { label: "National game, step 7", to: "/plans?track=national" },
  "Modular professional stadiums (Priority 18)": { label: "Professional game, step 7", to: "/plans?track=professional" },
  "Modular county stadiums (remaining counties)": { label: "Professional game, step 7", to: "/plans?track=professional" },
};

const SORTS = [  ["cost_per_professional_usd", "Cheapest to produce a pro"],
  ["cost_index", "Cost of living"],
  ["rent_ksh_month", "Rent"],
  ["population", "Population"],
  ["commercial_potential_usd", "Commercial potential"],
  ["uk_multiple", "Saving against England"],
];

export default function Facilities() {
  const [f, setF] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState("cost_per_professional_usd");
  const [proOnly, setProOnly] = useState(false);

  useSeo({
    title: "Grassroots Ecosystem And Development Costs",
    description:
      "Every proposed KickStartKenyaFootball facility priced on build cost and annual running cost, with its social, economic and global impact — plus what it costs to train, educate and produce a professional footballer in each of Kenya's 47 counties, compared with England.",
  });

  useEffect(() => {
    api.get("/facilities-review").then((r) => setF(r.data)).catch(() => setErr("We could not load the facility costs. Refresh to try again."));
    api.get("/development-costs").then((r) => setD(r.data)).catch(() => setErr("We could not load the development costs. Refresh to try again."));
  }, []);

  const rows = useMemo(() => {
    if (!d) return [];
    const list = proOnly ? d.rows.filter((r) => r.priority) : d.rows;
    const asc = sort === "cost_per_professional_usd" || sort === "cost_index" || sort === "rent_ksh_month";
    return [...list].sort((a, b) => (asc ? a[sort] - b[sort] : b[sort] - a[sort]));
  }, [d, sort, proOnly]);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="facilities-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!f || !d) return <div className="min-h-[60vh] bg-black" data-testid="facilities-loading" />;
  const t = f.totals;
  const m = d.model;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="facilities-page">
      <StageBar stage="build" />
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">Stage two — building the game</p>
            <h1 className="display-1 mt-5 max-w-4xl">The Grassroots Ecosystem</h1>
            <p className="body-copy mt-6 max-w-3xl">
              Every facility in the recommended steps, priced twice: the capital cost to build it and the annual cost to
              run it at Kenyan wage, power and maintenance rates. The step column tells you which recommended step puts it
              on the ground, so this page and the steps can never disagree with each other. Then the number that
              matters most to a country exporting talent — what it actually costs to train, educate and produce one
              professional footballer, county by county, against what an English Category 1 academy spends to do the
              same job.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-4">
            <a href={`${API}/research-pack.pdf`} className="btn-primary" data-testid="facilities-prospectus">
              <Download size={14} /> Download the research pack
            </a>
            <Link to="/money" className="btn-ghost">
              The money <ArrowUpRight size={14} />
            </Link>
            <Link to="/trading" className="btn-ghost" data-testid="facilities-trading-link">
              Player trading model <ArrowUpRight size={14} />
            </Link>
            <Link to="/plans" className="btn-ghost" data-testid="facilities-steps-link">
              The recommended steps that build these <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="facilities-headline">
            <Stat label="Capital cost, everything" value={usd(t.capex_usd)} note={`${usd(t.capex_low_usd)}–${usd(t.capex_high_usd)} range`} testId="facilities-capex" />
            <Stat label="Annual running cost" value={usd(t.opex_usd)} note={`${t.opex_as_pct_of_capex}% of build cost a year`} testId="facilities-opex" />
            <Stat label="Modelled trading income" value={usd(t.annual_revenue_usd)} note={`${t.cost_recovery_pct}% of running costs covered`} testId="facilities-revenue" />
            <Stat label="Ten-year whole-life cost" value={usd(t.ten_year_usd)} note={`${int(t.staff)} permanent jobs`} testId="facilities-tenyear" />
          </div>

          <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2" data-testid="facilities-classes">
            {(f.classes || []).map((c, i) => (
              <div key={c.class} className="bg-[#0b0b0b] p-6" data-testid={`facility-class-${i}`}>
                <p className="overline text-ochre">{c.class}</p>
                <p className="mono mt-3 text-2xl font-bold text-white md:text-3xl">{usd(c.capex_usd)}</p>
                <p className="mt-2 text-[0.66rem] uppercase tracking-[0.14em] text-zinc-400">
                  {usd(c.capex_low_usd)}–{usd(c.capex_high_usd)} range · {c.units} units · {c.lines} lines
                </p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  {usd(c.opex_usd)} a year to run, {usd(c.annual_revenue_usd)} of trading income
                  ({c.cost_recovery_pct}% cost recovery)
                  {c.players_served ? `, reaching ${int(c.players_served)} players a year` : ""}.
                </p>
              </div>
            ))}
          </div>

          <FacilityLibrary />

          <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="facilities-table">
            <table className="w-full min-w-[1180px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Facility", "Built by", "Class", "Units", "CAPEX", "Range", "OPEX a year", "OPEX %", "Trading income", "Cost recovery", "Life", "CAPEX per player"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {f.rows.map((r, i) => (
                  <tr key={r.item} className="border-b border-zinc-900" data-testid={`facility-row-${i}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      {r.item}
                      <span className="mt-1 block text-[0.6rem] uppercase tracking-[0.14em] text-zinc-500">{r.status}</span>
                      {r.benchmark ? (
                        <a
                          href={r.benchmark_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 block max-w-md text-[0.62rem] leading-relaxed text-ochre underline underline-offset-4"
                          data-testid={`facility-benchmark-${i}`}
                        >
                          Checked against: {r.benchmark}
                        </a>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-ochre">
                      <Link to={STEP_FOR[r.item]?.to || "/plans"} data-testid={`facility-step-${i}`} className="hover:text-white">
                        {STEP_FOR[r.item]?.label || "Recommended steps"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[0.6rem] uppercase tracking-[0.14em] text-maasai">{r.class}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.units}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.capex_usd)}</td>
                    <td className="mono px-4 py-3 text-xs text-zinc-500">{usd(r.capex_low_usd)}–{usd(r.capex_high_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.opex_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{pct(r.opex_pct)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.annual_revenue_usd)}</td>
                    <td className={`mono px-4 py-3 text-sm ${r.cost_recovery_pct >= 100 ? "text-ochre" : "text-zinc-400"}`}>
                      {r.cost_recovery_pct}%
                    </td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.life_years}y</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">
                      {r.capex_per_player_usd ? `$${r.capex_per_player_usd}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 max-w-4xl text-xs leading-relaxed text-zinc-400">
            {f.note} Every line carries a low, central and high estimate and the benchmark it was checked against;
            the headline figures use the central case.
          </p>

          <BrazilBenchmark />

          <h2 className="display-2 mt-16">Why each one earns its place</h2>
          <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 lg:grid-cols-2" data-testid="facilities-impacts">
            {f.rows.map((r, i) => (
              <div key={r.item} className="bg-[#0b0b0b] p-6 md:p-7" data-testid={`facility-impact-${i}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <p className="overline text-ochre">{r.item}</p>
                  <p className="mono text-[0.66rem] uppercase tracking-[0.14em] text-zinc-400">
                    {usd(r.capex_usd)} build · {usd(r.opex_usd)} a year
                  </p>
                </div>
                <dl className="mt-5 space-y-4 text-sm leading-relaxed">
                  {[["Social", r.social], ["Economic", r.economic], ["Global", r.global]].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-zinc-500">{k}</dt>
                      <dd className="mt-1 text-zinc-300">{v}</dd>
                    </div>
                  ))}
                  <div>
                    <dt className="text-[0.6rem] font-bold uppercase tracking-[0.18em] text-zinc-500">Running cost covers</dt>
                    <dd className="mt-1 text-zinc-400">{r.opex_items}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-20 px-5 md:px-10">
        <div className="mx-auto max-w-[1500px] border-t border-zinc-900 pt-14">
          <Reveal>
            <p className="eyebrow">Cost to produce a professional</p>
            <h2 className="display-2 mt-4 max-w-4xl">
              {usd(m.average_kenya_per_professional_usd)} in Kenya against {usd(m.uk_per_professional_usd)} in England
            </h2>
            <p className="body-copy mt-6 max-w-3xl">
              Coaching, schooling, food, medical cover, kit and competition, accumulated over the years a player
              actually spends in an academy, multiplied by the {m.players_per_professional} academy players it takes to
              produce one professional contract. Run the same model on an English Category 1 academy and it returns{" "}
              {usd(m.uk_per_professional_usd)} per professional — close to the published spend per academy graduate,
              which is the sanity check. Kenya produces the same professional for roughly{" "}
              <span className="text-white">1/{m.uk_multiple_average}</span> of that, and in the cheapest counties for a
              fraction again. The same talent, at a price no European academy system can match.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="development-headline">
            <Stat label="Average across the counties" value={usd(m.average_kenya_per_professional_usd)} note={`${m.players_per_professional} academy players per professional`} testId="dev-average" />
            <Stat label="Cheapest county" value={usd(m.cheapest_usd)} note={m.cheapest_county} testId="dev-cheapest" />
            <Stat label="Dearest county" value={usd(m.dearest_usd)} note={m.dearest_county} testId="dev-dearest" />
            <Stat label="England Category 1" value={usd(m.uk_per_professional_usd)} note={`${m.uk_players_per_professional} players per graduate`} testId="dev-uk" />
          </div>

          <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="development-items">
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Annual cost per academy player", "Kenya (USD)", "Kenya (KES)", "England (USD)", "England multiple"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.items.map((i) => (
                  <tr key={i.item} className="border-b border-zinc-900">
                    <td className="px-4 py-3 text-sm text-white">{i.item}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">${int(i.kenya_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{ksh(i.kenya_ksh)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">${int(i.uk_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{i.uk_multiple}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-14 flex flex-wrap items-end justify-between gap-5">
            <div>
              <h3 className="display-3">Compare every county</h3>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">
                Each county is priced with its own cost-of-living index, built from its census density and its GDP at
                purchasing power parity per head — so wages, rent, transport and food are local, not national.
                Commercial potential is the modelled annual ceiling for gate receipts, sponsorship, appearances and
                player trading in that county.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor="dev-sort" className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400">
                Sort by
              </label>
              <select
                id="dev-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="development-sort"
                className="border border-white/20 bg-black px-3 py-2 text-sm text-white outline-none focus:border-ochre"
              >
                {SORTS.map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setProOnly((v) => !v)}
                data-testid="development-pro-filter"
                className={`border px-4 py-2 text-[0.6rem] font-bold uppercase tracking-[0.16em] transition-colors ${
                  proOnly ? "border-maasai bg-maasai text-white" : "border-white/20 text-zinc-300 hover:border-ochre"
                }`}
              >
                Priority 18 only
              </button>
            </div>
          </div>

          <div className="mt-8 overflow-x-auto border border-zinc-800" data-testid="development-table">
            <table className="w-full min-w-[1000px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["County", "Rent a month", "Cost index", "Per player a year", "Average tenure per player", "Per professional (USD)", "Per professional (KES)", "vs England", "Commercial potential", "Pros funded by it"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.county} className="border-b border-zinc-900" data-testid={`development-row-${i}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      {r.county}
                      {r.priority ? (
                        <span className="ml-2 border border-maasai px-1.5 py-0.5 text-[0.5rem] font-bold uppercase tracking-[0.14em] text-maasai">
                          P18
                        </span>
                      ) : null}
                    </td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300" data-testid={`development-rent-${i}`}>
                      KSh {int(r.rent_ksh_month)}
                    </td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">x{r.cost_index}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">${int(r.annual_per_player_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.ten_year_per_player_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.cost_per_professional_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{ksh(r.cost_per_professional_ksh)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.uk_multiple}x cheaper</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.commercial_potential_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.pros_funded_by_commercial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PriorityEighteen />
          <CountyAllocation />

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="development-formula">
              <p className="overline text-ochre">The formula</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">{d.formula}</p>
            </div>
            <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="development-sources">
              <p className="overline">Sources</p>
              <SourcesLink testId="facilities-sources-link" className="!mt-3" />
              <div className="mt-4 flex flex-col gap-3 text-xs text-zinc-300">
                {d.sources.map((s) => (
                  <div key={s.url + s.label}>
                    <a href={s.url} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ochre">
                      {s.source}
                    </a>
                    <DataStamp source={s} className="!mt-2" testId={`facilities-source-stamp-${s.verification}`} />
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-zinc-400">
                Every cost, index and commercial figure on this page is our own estimate, published so it can be argued
                with. Nothing is a quotation, a budget or an awarded contract.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FutsalCourtFeature />
      <KplAlignment show="facilities" />
      <StageClose stage="build" />
    </div>
  );
}
